export type ShelfItemOverflowPolicy = "compress-to-boundary" | "extend-on-overflow";

export interface ShelfWidthLike {
  width: number;
  merchandisableWidth?: number | null;
}

export interface ShelfItemWidthLike {
  getHorizontalFacings: () => number;
  getWidth: () => number;
  getXCappingWidth: () => number;
}

export const getShelfItemWidth = (item: ShelfItemWidthLike) =>
  item.getWidth() * item.getHorizontalFacings() + item.getXCappingWidth();

export const getEffectiveMerchandisableWidth = ({ merchandisableWidth, width }: ShelfWidthLike) =>
  Math.max(0, Math.min(merchandisableWidth ?? width, width));

/**
 * Merged shelf groups are only valid when every member is fully merchandisable
 * (effectiveMerchandisableWidth === width). Partial and zero-width shelves must
 * not be merged, so this is the single gate merge/unmerge call sites rely on.
 */
export const isFullyMerchandisable = (shelf: ShelfWidthLike) =>
  getEffectiveMerchandisableWidth(shelf) === shelf.width;

export interface ShelfResizeState {
  merchandisableWidth?: number | null;
  width: number;
}

/**
 * Applies the agreed resize semantics for shelf width vs. merchandisableWidth:
 * - a fully merchandisable shelf stays fully merchandisable at the new width
 * - a partial/zero shelf keeps its absolute merchandisable amount, clamped to
 *   the new width (so shrinking below it makes the shelf fully merchandisable
 *   again, and it can never exceed the new physical width)
 * - shelves with no explicit merchandisableWidth (null/undefined) remain so,
 *   since that already means "full physical width" at any width
 */
export const resizeShelfWidth = (
  shelf: ShelfResizeState,
  newWidth: number,
): number | null | undefined => {
  const { merchandisableWidth, width: previousWidth } = shelf;
  if (merchandisableWidth === null || merchandisableWidth === undefined) {
    return merchandisableWidth;
  }

  const wasFullyMerchandisable = getEffectiveMerchandisableWidth(shelf) === previousWidth;
  return wasFullyMerchandisable ? newWidth : Math.min(merchandisableWidth, newWidth);
};

export const getMergedMerchandisableWidth = (shelves: readonly ShelfWidthLike[]) =>
  shelves.reduce((total, shelf) => total + getEffectiveMerchandisableWidth(shelf), 0);

export const getNonMerchandisableArea = (shelf: ShelfWidthLike) => {
  const xOffset = getEffectiveMerchandisableWidth(shelf);

  return {
    width: shelf.width - xOffset,
    xOffset,
  };
};

export const getMergedNonMerchandisableArea = (shelves: readonly ShelfWidthLike[]) => {
  const width = shelves.reduce((total, shelf) => total + shelf.width, 0);
  const xOffset = getMergedMerchandisableWidth(shelves);

  return {
    width: width - xOffset,
    xOffset,
  };
};

/**
 * Returns historical between-packed item offsets within the merchandising boundary.
 * The model compresses overflow to that boundary, while Konva preserves natural widths.
 * A non-positive merchandising boundary still lays out existing items from the origin;
 * this positioning-only fallback does not change the effective merchandisable width.
 */
export const getShelfItemXOffsets = ({
  itemWidths,
  merchandisableWidth,
  overflowPolicy,
}: {
  itemWidths: readonly number[];
  merchandisableWidth: number;
  overflowPolicy: ShelfItemOverflowPolicy;
}) => {
  const itemsWidth = itemWidths.reduce((total, width) => total + width, 0);
  const positioningBoundary = merchandisableWidth > 0 ? merchandisableWidth : itemsWidth;
  const rawGap =
    itemWidths.length > 1 ? (positioningBoundary - itemsWidth) / (itemWidths.length - 1) : 0;
  const gap = overflowPolicy === "extend-on-overflow" ? Math.max(0, rawGap) : rawGap;
  let x = 0;

  return itemWidths.map((width) => {
    const offset = x;
    x += width + gap;
    return offset;
  });
};
